import {
    activeDataTableFilters,
    type VxDataTableColumn,
    type VxDataTableFilter,
    type VxDataTableFilterField,
    type VxDataTableFilterValue,
    type VxDataTableSort,
} from '@voxie/frontend-components';
import type {
    KioskTeamSsoLogin,
    KioskTeamSsoLoginFilterKey,
    KioskTeamSsoLoginFilterOperator,
    KioskTeamSsoLoginFilters,
    KioskTeamSsoLoginResult,
    KioskTeamSsoLoginsCountParams,
    KioskTeamSsoLoginsParams,
} from '~/services/adminSsoClient.types';
import type { SsoLoginMethod } from '~/services/ssoClient.types';

const KIOSK_TEAM_SSO_LOGINS_PER_PAGE = 50;
const KIOSK_TEAM_SSO_LOGINS_SEARCH_MAX_LENGTH = 80;
export const KIOSK_TEAM_SSO_LOGINS_MAX_FILTER_VALUES = 10;

export type KioskTeamSsoLoginsSort = VxDataTableSort<KioskTeamSsoLogin>;
export type KioskTeamSsoLoginsFilter = VxDataTableFilter<KioskTeamSsoLogin>;

export const kioskTeamSsoLoginColumns: VxDataTableColumn<KioskTeamSsoLogin>[] = [
    { key: 'created_at', label: 'Time', width: 170, sortable: true, hideable: false },
    { key: 'email', label: 'Email', width: 240 },
    { key: 'user_id', label: 'User', width: 200 },
    { key: 'result', label: 'Result', width: 110 },
    { key: 'method', label: 'Method', width: 140 },
    { key: 'failure_reason', label: 'Failure reason', width: 240 },
    { key: 'ip_address', label: 'IP address', width: 150 },
    { key: 'user_agent', label: 'Browser', width: 260, visible: false },
];

export const columnsStorageKey = (teamId: number): string => `voxie:kiosk:team-sso-logins:v1:${teamId}:columns`;

export const densityStorageKey = (teamId: number): string => `voxie:kiosk:team-sso-logins:v1:${teamId}:density`;

export const defaultSort = { key: 'created_at', direction: 'desc' } as const satisfies NonNullable<
    VxDataTableSort<KioskTeamSsoLogin>
>;

interface FilterOption<TValue extends string> extends VxDataTableFilterValue {
    value: TValue;
    label: string;
}

export const METHOD_LABELS: Record<SsoLoginMethod, string> = {
    password: 'Password',
    sso: 'SSO',
    impersonation: 'Impersonation',
    emergency_token: 'Emergency token',
};

const isLoginMethod = (value: string): value is SsoLoginMethod => value in METHOD_LABELS;

export const methodLabel = (method: string | null | undefined): string => {
    if (!method) {
        return '-';
    }

    return isLoginMethod(method) ? METHOD_LABELS[method] : method;
};

// The refusal codes of SsoLoginRefused plus the reasons the password, two-factor and emergency logins record.
export const FAILURE_REASON_LABELS: Record<string, string> = {
    access_denied: 'Identity provider denied access',
    account_mismatch: 'Logged in as another account',
    connected_apps_unavailable: 'Connected apps not available for the team',
    connection_disabled: 'Connection disabled',
    credentials: 'Wrong email or password',
    deprovisioned: 'Deprovisioned at the identity provider',
    email_conflict: 'Email belongs to another account',
    email_lookalike: 'Look-alike email',
    email_missing: 'No email in the response',
    email_not_verified_domain: 'Email outside verified domains',
    email_unverified: 'Email not verified at the identity provider',
    emergency_token: 'Emergency token rejected',
    expired: 'Response expired',
    idp_error: 'Identity provider error',
    idp_initiated_disabled: 'Identity provider-initiated login not allowed',
    idp_unavailable: 'Identity provider unreachable',
    invalid_response: 'Invalid response',
    invalid_signature: 'Invalid signature',
    mobile_unavailable: 'Mobile app not available for the team',
    not_invited: 'Invitation not usable',
    platform_admin: 'Platform admins cannot use SSO',
    replayed: 'Response replayed',
    state_invalid: 'Login state invalid',
    suspended: 'Account suspended',
    two_factor_code: 'Wrong two-factor code',
    wrong_audience: 'Response for another team',
};

export const failureReasonLabel = (reason: string | null | undefined): string =>
    reason ? FAILURE_REASON_LABELS[reason] ?? reason : '-';

const resultOptions: FilterOption<KioskTeamSsoLoginResult>[] = [
    { value: 'success', label: 'Success' },
    { value: 'failed', label: 'Failed' },
];

const methodOptions: FilterOption<SsoLoginMethod>[] = (Object.entries(METHOD_LABELS) as [SsoLoginMethod, string][]).map(
    ([value, label]) => ({ value, label })
);

const failureReasonOptions: FilterOption<string>[] = Object.entries(FAILURE_REASON_LABELS).map(([value, label]) => ({
    value,
    label,
}));

export const kioskTeamSsoLoginFilterFields: VxDataTableFilterField<KioskTeamSsoLogin>[] = [
    { key: 'result', label: 'Result', values: resultOptions },
    { key: 'method', label: 'Method', values: methodOptions },
    { key: 'failure_reason', label: 'Failure reason', values: failureReasonOptions },
];

const filterKeys: string[] = kioskTeamSsoLoginFilterFields.map((field) => field.key);

const isFilterKey = (key: string): key is KioskTeamSsoLoginFilterKey => filterKeys.includes(key);

const isFilterOperator = (operator: string): operator is KioskTeamSsoLoginFilterOperator =>
    operator === 'in' || operator === 'not_in';

export const toFilterParams = (filters: KioskTeamSsoLoginsFilter[]): KioskTeamSsoLoginFilters => {
    const params: KioskTeamSsoLoginFilters = {};

    activeDataTableFilters(filters).forEach((filter) => {
        if (isFilterKey(filter.key) && isFilterOperator(filter.operator)) {
            params[filter.key] = { operator: filter.operator, values: filter.values };
        }
    });

    return params;
};

export const exceedsFilterValueLimit = (filters: KioskTeamSsoLoginsFilter[]): boolean =>
    filters.some((filter) => filter.values.length > KIOSK_TEAM_SSO_LOGINS_MAX_FILTER_VALUES);

export const toSearchQuery = (search: string): string =>
    Array.from(search.trim()).slice(0, KIOSK_TEAM_SSO_LOGINS_SEARCH_MAX_LENGTH).join('').trimEnd();

type SortParams = Required<Pick<KioskTeamSsoLoginsParams, 'sort_dir'>>;

export const toSortParams = (sort: KioskTeamSsoLoginsSort): SortParams => ({
    sort_dir: sort?.direction ?? defaultSort.direction,
});

export const toLoginsCountParams = (
    query: string,
    filters: KioskTeamSsoLoginsFilter[]
): KioskTeamSsoLoginsCountParams => {
    const filterParams = toFilterParams(filters);

    return {
        ...(query === '' ? {} : { query }),
        ...(Object.keys(filterParams).length === 0 ? {} : { filters: filterParams }),
    };
};

export const toLoginsParams = (
    query: string,
    sort: KioskTeamSsoLoginsSort,
    filters: KioskTeamSsoLoginsFilter[]
): KioskTeamSsoLoginsParams => ({
    ...toLoginsCountParams(query, filters),
    ...toSortParams(sort),
    per_page: KIOSK_TEAM_SSO_LOGINS_PER_PAGE,
});

export const isNarrowed = (params: KioskTeamSsoLoginsCountParams): boolean =>
    params.query !== undefined || params.filters !== undefined;

export const nextCursorFrom = (nextPageUrl: string | null): string | null =>
    nextPageUrl === null ? null : new URLSearchParams(nextPageUrl.split('?')[1]).get('cursor');
